/**
 * Shared constants for the Personal Trainer App
 * This file contains all shared constants that are used
 * across web and mobile applications.
 */

import { getEnvBool, getEnvSafe } from '../utils/env';

// =============================================================================
// API CONFIGURATION
// =============================================================================

export const API_CONFIG = {
  BASE_URL: getEnvSafe('VITE_API_BASE_URL', 'http://localhost:8000/api'),
  AUTH_BASE_URL: getEnvSafe('VITE_AUTH_BASE_URL', 'http://localhost:8000'),
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
} as const;

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  PAYMENT_REQUIRED: 402,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  LOCKED: 423,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
} as const;

// =============================================================================
// STORAGE CONSTANTS
// =============================================================================

export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_PREFERENCES: 'user_preferences',
  THEME: 'theme',
  LANGUAGE: 'language',
  WORKOUT_DRAFT: 'workout_draft',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  SIDEBAR_EXPANDED: 'sidebar-expanded',
} as const;

// =============================================================================
// SESSION TIMEOUT CONSTANTS
// =============================================================================

export const SESSION_TIMEOUT = {
  // Cookie-based sessions (API server sessions) - Web app only
  COOKIE_BASED: 120 * 60 * 1000, // 120 minutes (2 hours)
  COOKIE_WARNING_INTERVALS: [
    15 * 60 * 1000,
    10 * 60 * 1000,
    5 * 60 * 1000,
    2 * 60 * 1000,
    60 * 1000,
  ], // 15, 10, 5, 2, 1 minutes

  // Token-based sessions (JWT tokens) - Mobile app only
  // Note: Mobile app handles token refresh automatically, no warning intervals needed
  TOKEN_BASED: 30 * 24 * 60 * 60 * 1000, // 30 days
} as const;

// =============================================================================
// WORKOUT CONSTANTS
// =============================================================================

export const WORKOUT_CONSTANTS = {
  DEFAULT_REST_TIME: 60, // in seconds
  DEFAULT_SETS: 0,
  DEFAULT_REPS: 0,
  MAX_EXERCISES_PER_WORKOUT: 20,
  MAX_SETS_PER_EXERCISE: 10,
  SESSION_STORAGE_KEY: 'workout_session_state',
  SESSION_SERIALIZATION_VERSION: '1.0.0',
  WORKOUT_CACHE_TTL: 24 * 60 * 60 * 1000, // 24 hours
} as const;

// =============================================================================
// DATE CONSTANTS
// =============================================================================

// Defined in a leaf module so utils/date can read them without importing this
// file.
export { DATE_CONSTANTS } from './date-constants';

// =============================================================================
// NOTIFICATION CONSTANTS
// =============================================================================

export const NOTIFICATION_CONSTANTS = {
  CUSTOM_USER_PRIVATE_CHANNEL: 'users.username',
  LARAVEL_USER_PRIVATE_CHANNEL: 'App.Models.User.id',
  LIVE_NOTIFICATIONS: getEnvBool('VITE_ENABLE_LIVE_NOTIFICATIONS', {
    defaultValue: false,
  }), // notification kill switch
} as const;

// =============================================================================
// JOB PROGRESS CONSTANTS
// =============================================================================

export const JOB_PROGRESS_CONSTANTS = {
  /**
   * Echo channel template for a user's job-progress stream. `username` is the
   * placeholder; the `private-` prefix is added by Echo's private subscription.
   */
  USER_JOBS_PRIVATE_CHANNEL: 'user.username.jobs',
  /** Custom `broadcastAs` event — listen with a leading dot in Echo. */
  JOB_PROGRESS_EVENT: '.job.progress.updated',
  /** Job-progress kill switch (independent of the notifications switch). */
  LIVE_JOB_PROGRESS: getEnvBool('VITE_ENABLE_LIVE_JOB_PROGRESS', {
    defaultValue: false,
  }),
} as const;

// =============================================================================
// AUTH CONSTANTS
// =============================================================================

export const AUTH_CONSTANTS = {
  /**
   * Whether Google and Apple sign in, on sign-in and registration: the ways
   * in that could skip an invitation. Off during the invitation-only beta.
   * The API refuses social sign-ups on its own, so this only hides the
   * controls. Confirming it's you with a provider follows it, since only a
   * social sign-up makes an account without a password. Mobile reads it as
   * `EXPO_PUBLIC_ENABLE_SOCIAL_SIGN_IN`.
   */
  SOCIAL_SIGN_IN: getEnvBool('VITE_ENABLE_SOCIAL_SIGN_IN', {
    defaultValue: false,
  }),
  /**
   * Whether a signed-in account can connect and disconnect Google or Apple in
   * Settings. On unless turned off: connecting skips no invitation, and an
   * invited account is its owner's to manage. Mobile reads it as
   * `EXPO_PUBLIC_ENABLE_SOCIAL_LINKING`.
   */
  SOCIAL_LINKING: getEnvBool('VITE_ENABLE_SOCIAL_LINKING', {
    defaultValue: true,
  }),
  /**
   * Whether Settings offers Change email. On unless turned off. A change
   * already waiting can still be cancelled. Mobile reads it as
   * `EXPO_PUBLIC_ENABLE_EMAIL_CHANGE`.
   */
  EMAIL_CHANGE: getEnvBool('VITE_ENABLE_EMAIL_CHANGE', {
    defaultValue: true,
  }),
} as const;
